import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.must_change_password) {
        navigate("/set-password");
      } else {
        toast.success("Signed in");
        navigate("/");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 grain">
      <div className="w-full max-w-md relative z-10 fade-up">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="border border-white/10 bg-[#121212] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-1">
            <Lock size={18} className="text-[#FF3B30]" />
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">Admin Access</span>
          </div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter mb-8">Sign In</h1>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Email</Label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#FF3B30]" placeholder="you@troytested.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#FF3B30]" placeholder="••••••••" />
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={busy}
              className="w-full bg-white text-black font-bold py-3.5 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
              {busy ? "Signing in..." : "Enter"}
            </button>
            <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/forgot-password")}
              className="w-full text-center text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors">
              Forgot password?
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
